/**
 * ============================================================================
 * RiskEntry — Phase 7 frontend (Quality Risk Management Module, ICH Q9).
 * Owner: Phase 7 — risk-svc.
 * Route: /risks/:id
 *
 * Purpose: FMEA editor + risk detail view. Three blocks:
 *   1) FMEA editor — sev / occ / det sliders + live RPN preview + level chip.
 *      Submit appends a new score-history row (RSK-001 monotonic).
 *   2) Score history timeline — every row that has ever been written for this
 *      risk, in chronological order. Read-only — server enforces append-only.
 *   3) Review schedule — last reviewed + next review due + a "Record review"
 *      action wired to POST /api/v1/risks/{id}/review.
 *
 * Critical-accept flow: when the level is CRITICAL and the user clicks
 *  "Accept", a modal dialog requests TWO distinct signature ids (RSK-007).
 *  The server enforces the constraint a second time (DB CHECK + Java).
 *
 * URS rows: RSK-001 (history), RSK-004 (bands), RSK-005 (cadence), RSK-007
 *           (dual-sign), RSK-009 (detail view).
 * ============================================================================
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHero } from '@edms/design-system';
import wstyles from '../WebhookAdmin/WebhookAdmin.module.css';
import styles from './RiskEntry.module.css';
import { useRiskDetailQuery } from '../RiskRegister/useRiskDetailQuery';
import {
  FMEA_MAX,
  FMEA_MIN,
  computeRpn,
  levelDescription,
  levelFromRpn,
} from '../RiskRegister/rpn';
import type { RiskLevel } from '../RiskRegister/useRisksQuery';
import { apiFetch } from '../../api/client';
import { RelatedRecords } from '../../components/RelatedRecords';

const LEVEL_CLASS: Record<RiskLevel, string> = {
  CRITICAL: styles.levelCritical,
  HIGH: styles.levelHigh,
  MEDIUM: styles.levelMedium,
  LOW: styles.levelLow,
};

export interface RiskEntryProps {
  riskId: string;
  onBack?: () => void;
}

export function RiskEntry({ riskId, onBack }: RiskEntryProps) {
  const { t, i18n } = useTranslation();
  const detailQ = useRiskDetailQuery(riskId);
  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }),
    [i18n.language],
  );

  // FMEA editor local state — primed from the loaded detail.
  const [sev, setSev] = useState<number>(3);
  const [occ, setOcc] = useState<number>(3);
  const [det, setDet] = useState<number>(3);
  const [rationale, setRationale] = useState<string>('');
  const [showAccept, setShowAccept] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [reviewOutcome, setReviewOutcome] = useState<string>('UNCHANGED');
  const [reviewRationale, setReviewRationale] = useState('');
  const [primarySig, setPrimarySig] = useState('');
  const [secondarySig, setSecondarySig] = useState('');
  const [acceptRationale, setAcceptRationale] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  // When the detail loads, prime the slider state from the current scores.
  // useMemo on the loaded data lets us avoid an extra useEffect.
  const seeded = useMemo(() => {
    if (detailQ.data) {
      setSev(detailQ.data.severityScore);
      setOcc(detailQ.data.occurrenceScore);
      setDet(detailQ.data.detectabilityScore);
    }
    return true;
    // intentionally only react to the first data load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailQ.data?.riskId]);
  void seeded;

  const previewRpn = computeRpn(sev, occ, det);
  const previewLevel = levelFromRpn(previewRpn);

  async function submitScore() {
    if (rationale.trim().length < 10) {
      setServerError(
        t('risk.entry.rationaleTooShort', {
          defaultValue: 'Rationale must be at least 10 characters (RSK-001 audit record).',
        }) as string,
      );
      return;
    }
    setSubmitting(true);
    setServerError(null);
    try {
      await apiFetch<unknown>(`/api/v1/risks/${encodeURIComponent(riskId)}/score`, {
        method: 'POST',
        body: {
          severityScore: sev,
          occurrenceScore: occ,
          detectabilityScore: det,
          rationale,
        },
      });
      await detailQ.refetch();
      setRationale('');
    } catch (e) {
      setServerError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function submitAccept() {
    if (acceptRationale.trim().length < 50) {
      setServerError(
        t('risk.entry.acceptRationaleTooShort', {
          defaultValue:
            'Acceptance rationale must be at least 50 characters (RSK-007 audit record).',
        }) as string,
      );
      return;
    }
    if (detailQ.data?.riskLevel === 'CRITICAL') {
      if (!primarySig || !secondarySig) {
        setServerError(
          t('risk.entry.dualSignRequired', {
            defaultValue:
              'CRITICAL risk acceptance requires TWO distinct signatures (RSK-007).',
          }) as string,
        );
        return;
      }
      if (primarySig === secondarySig) {
        setServerError(
          t('risk.entry.dualSignDistinct', {
            defaultValue:
              'The two signatures must be DISTINCT (RSK-007 dual-sign).',
          }) as string,
        );
        return;
      }
    }
    setSubmitting(true);
    setServerError(null);
    try {
      await apiFetch<unknown>(`/api/v1/risks/${encodeURIComponent(riskId)}/accept`, {
        method: 'POST',
        body: {
          primarySignatureId: primarySig,
          secondarySignatureId: secondarySig || null,
          rationale: acceptRationale,
        },
      });
      await detailQ.refetch();
      setShowAccept(false);
      setPrimarySig('');
      setSecondarySig('');
      setAcceptRationale('');
    } catch (e) {
      setServerError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  // RSK-005: record a periodic review (real PG → risk.risk_reviews). (drafts/61 F1-8.)
  async function submitReview() {
    if (reviewRationale.trim().length < 10) {
      setServerError(t('risk.entry.review.rationaleRequired', {
        defaultValue: 'A review rationale of at least 10 characters is required.',
      }));
      return;
    }
    setSubmitting(true);
    setServerError(null);
    try {
      await apiFetch<unknown>(`/api/v1/risks/${encodeURIComponent(riskId)}/review`, {
        method: 'POST',
        body: { outcome: reviewOutcome, rationale: reviewRationale.trim() },
      });
      await detailQ.refetch();
      setShowReview(false);
      setReviewRationale('');
      setReviewOutcome('UNCHANGED');
    } catch (e) {
      setServerError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  if (detailQ.isLoading) {
    return (
      <div className={wstyles.adminScreen}>
        <p>{t('common.loading', { defaultValue: 'Loading…' })}</p>
      </div>
    );
  }

  if (detailQ.isError || !detailQ.data) {
    return (
      <div className={wstyles.adminScreen}>
        <div className={styles.errorEnvelope}>
          <p>{t('risk.entry.error', { defaultValue: 'Failed to load risk.' })}</p>
          <button type="button" onClick={() => detailQ.refetch()}>
            {t('common.retry', { defaultValue: 'Retry' })}
          </button>
        </div>
      </div>
    );
  }

  const d = detailQ.data;
  const isTerminal = d.status === 'CLOSED' || d.status === 'ACCEPTED';

  return (
    <div className={wstyles.adminScreen}>
      <PageHero
        eyebrow={t('risk.entry.eyebrow', { defaultValue: 'Quality Events' })}
        title={
          <>
            <span className={styles.riskNumber}>{d.riskNumber}</span> {d.title}
          </>
        }
        subtitle={
          <>
            {d.processScope} · {d.riskCategory}
            <span className={`${styles.levelChip} ${LEVEL_CLASS[d.riskLevel]}`}>
              <span className={styles.dot} aria-hidden /> {d.riskLevel}
            </span>
            <span className={styles.statusChip}>{d.status}</span>
          </>
        }
        actions={
          onBack ? (
            <button type="button" onClick={onBack} className={styles.backBtn}>
              {t('common.back', { defaultValue: '← Back' })}
            </button>
          ) : undefined
        }
      />

      <section className={styles.descBlock} aria-label="Description">
        <h2>{t('risk.entry.description', { defaultValue: 'Description' })}</h2>
        <p>{d.description}</p>
      </section>

      <section className={styles.editorBlock} aria-label="FMEA editor">
        <h2>{t('risk.entry.fmeaEditor', { defaultValue: 'FMEA score (1..5 each)' })}</h2>
        <div className={styles.sliderGrid}>
          <SliderField
            label={t('risk.entry.severity', { defaultValue: 'Severity' }) as string}
            value={sev}
            onChange={setSev}
          />
          <SliderField
            label={t('risk.entry.occurrence', { defaultValue: 'Occurrence' }) as string}
            value={occ}
            onChange={setOcc}
          />
          <SliderField
            label={t('risk.entry.detectability', { defaultValue: 'Detectability (5 = undetectable)' }) as string}
            value={det}
            onChange={setDet}
          />
        </div>
        <div className={styles.rpnPreview}>
          <span className={styles.rpnPreviewLabel}>RPN</span>
          <span className={styles.rpnPreviewValue}>{previewRpn}</span>
          <span className={`${styles.levelChip} ${LEVEL_CLASS[previewLevel]}`}>
            <span className={styles.dot} aria-hidden /> {previewLevel}
          </span>
          <span className={styles.rpnLevelHelp}>{levelDescription(previewLevel)}</span>
        </div>
        <label className={styles.rationaleField}>
          <span>{t('risk.entry.rationaleLabel', { defaultValue: 'Rationale (≥10 chars)' })}</span>
          <textarea
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            rows={3}
            disabled={isTerminal || submitting}
            placeholder={
              t('risk.entry.rationalePlaceholder', {
                defaultValue:
                  'Document why this score is appropriate (e.g. "Q2 inspection found new failure mode").',
              }) as string
            }
          />
        </label>
        {serverError && <div className={styles.errorInline} role="alert">{serverError}</div>}
        <div className={styles.actionsRow}>
          <button
            type="button"
            onClick={submitScore}
            disabled={isTerminal || submitting}
            className={styles.primaryBtn}
          >
            {t('risk.entry.appendScore', { defaultValue: 'Append score (RSK-001)' })}
          </button>
          {!isTerminal && (
            <button
              type="button"
              onClick={() => setShowAccept(true)}
              className={styles.acceptBtn}
              disabled={submitting}
            >
              {t('risk.entry.accept', { defaultValue: 'Accept risk' })}
            </button>
          )}
        </div>
      </section>

      <section className={styles.timelineBlock} aria-label="Score history">
        <h2>{t('risk.entry.scoreHistory', { defaultValue: 'Score history (append-only)' })}</h2>
        {d.scoreHistory.length === 0 && (
          <p className={styles.muted}>
            {t('risk.entry.scoreHistoryEmpty', { defaultValue: 'No history yet.' })}
          </p>
        )}
        <ul className={styles.timeline}>
          {d.scoreHistory.map((h) => (
            <li key={h.scoreId} className={styles.timelineItem}>
              <div className={styles.timelineDot} data-level={h.riskLevel} />
              <div className={styles.timelineBody}>
                <div className={styles.timelineHeader}>
                  <span className={styles.timelineDate}>{dateFmt.format(new Date(h.scoredAtUtc))}</span>
                  <span className={`${styles.levelChip} ${LEVEL_CLASS[h.riskLevel]}`}>
                    <span className={styles.dot} aria-hidden /> {h.riskLevel}
                  </span>
                </div>
                <div className={styles.timelineScores}>
                  S={h.severityScore} · O={h.occurrenceScore} · D={h.detectabilityScore} · RPN={h.rpn}
                </div>
                {h.rationale && <p className={styles.timelineRationale}>{h.rationale}</p>}
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className={styles.reviewBlock} aria-label="Review schedule">
        <h2>{t('risk.entry.reviewSchedule', { defaultValue: 'Review schedule (RSK-005)' })}</h2>
        <dl className={styles.reviewDl}>
          <dt>{t('risk.entry.lastReviewed', { defaultValue: 'Last reviewed' })}</dt>
          <dd>
            {d.lastReviewedUtc
              ? dateFmt.format(new Date(d.lastReviewedUtc))
              : t('common.dash', { defaultValue: '—' })}
          </dd>
          <dt>{t('risk.entry.nextReview', { defaultValue: 'Next review due' })}</dt>
          <dd>
            {d.nextReviewDueUtc
              ? dateFmt.format(new Date(d.nextReviewDueUtc))
              : t('common.dash', { defaultValue: '—' })}
          </dd>
        </dl>
        {!showReview ? (
          <button
            type="button"
            className={styles.recordReviewBtn}
            style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-primary, #1d4ed8)', color: '#fff', cursor: 'pointer', fontWeight: 600 }}
            onClick={() => setShowReview(true)}
          >
            {t('risk.entry.review.record', { defaultValue: 'Record review' })}
          </button>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: '32rem', marginTop: 8 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.875rem' }}>
              {t('risk.entry.review.outcome', { defaultValue: 'Outcome' })}
              <select value={reviewOutcome} onChange={(e) => setReviewOutcome(e.target.value)}>
                <option value="UNCHANGED">UNCHANGED</option>
                <option value="INCREASED">INCREASED</option>
                <option value="DECREASED">DECREASED</option>
                <option value="ACCEPTED">ACCEPTED</option>
                <option value="CLOSED">CLOSED</option>
              </select>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.875rem' }}>
              {t('risk.entry.review.rationale', { defaultValue: 'Rationale (≥ 10 chars)' })}
              <textarea value={reviewRationale} onChange={(e) => setReviewRationale(e.target.value)} rows={3} />
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={() => { setShowReview(false); setServerError(null); }} disabled={submitting}>
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </button>
              <button type="button" onClick={() => void submitReview()} disabled={submitting}
                style={{ background: 'var(--color-primary, #1d4ed8)', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontWeight: 600, cursor: 'pointer' }}>
                {submitting ? t('common.submitting', { defaultValue: 'Submitting…' }) : t('risk.entry.review.submit', { defaultValue: 'Submit review' })}
              </button>
            </div>
          </div>
        )}
        <h3 className={styles.subTitle}>
          {t('risk.entry.reviewHistory', { defaultValue: 'Review history' })}
        </h3>
        {d.reviewHistory.length === 0 && (
          <p className={styles.muted}>
            {t('risk.entry.reviewHistoryEmpty', { defaultValue: 'No reviews recorded yet.' })}
          </p>
        )}
        <ul className={styles.timeline}>
          {d.reviewHistory.map((rv) => (
            <li key={rv.reviewId} className={styles.timelineItem}>
              <div className={styles.timelineDot} data-outcome={rv.outcome} />
              <div className={styles.timelineBody}>
                <div className={styles.timelineHeader}>
                  <span className={styles.timelineDate}>{dateFmt.format(new Date(rv.reviewedAtUtc))}</span>
                  <span className={styles.outcomeChip}>{rv.outcome}</span>
                </div>
                <p className={styles.timelineRationale}>{rv.rationale}</p>
              </div>
            </li>
          ))}
        </ul>
      </section>

      {/* C-4 — cross-module Related Records (event-linking engine). */}
      <RelatedRecords entityType="RISK" entityId={riskId} />

      {showAccept && (
        <div className={styles.modalBackdrop} role="dialog" aria-modal>
          <div className={styles.modal}>
            <h2>{t('risk.entry.acceptTitle', { defaultValue: 'Accept risk (terminal)' })}</h2>
            <p className={styles.modalCopy}>
              {d.riskLevel === 'CRITICAL'
                ? t('risk.entry.acceptCriticalCopy', {
                    defaultValue:
                      'This is a CRITICAL risk. Two DISTINCT signatures are required (RSK-007).',
                  })
                : t('risk.entry.acceptNonCriticalCopy', {
                    defaultValue:
                      'A primary signature is required to formally accept this risk.',
                  })}
            </p>
            <label className={styles.modalField}>
              <span>{t('risk.entry.primarySignature', { defaultValue: 'Primary signature id' })}</span>
              <input value={primarySig} onChange={(e) => setPrimarySig(e.target.value)} />
            </label>
            {d.riskLevel === 'CRITICAL' && (
              <label className={styles.modalField}>
                <span>{t('risk.entry.secondarySignature', { defaultValue: 'Secondary signature id (distinct)' })}</span>
                <input value={secondarySig} onChange={(e) => setSecondarySig(e.target.value)} />
              </label>
            )}
            <label className={styles.modalField}>
              <span>{t('risk.entry.acceptRationale', { defaultValue: 'Acceptance rationale (≥50 chars)' })}</span>
              <textarea
                value={acceptRationale}
                onChange={(e) => setAcceptRationale(e.target.value)}
                rows={4}
              />
            </label>
            {serverError && <div className={styles.errorInline} role="alert">{serverError}</div>}
            <div className={styles.modalActions}>
              <button type="button" onClick={() => setShowAccept(false)}>
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </button>
              <button type="button" onClick={submitAccept} disabled={submitting} className={styles.primaryBtn}>
                {t('risk.entry.confirmAccept', { defaultValue: 'Confirm accept' })}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SliderField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <label className={styles.sliderField}>
      <span className={styles.sliderLabel}>{label}</span>
      <input
        type="range"
        min={FMEA_MIN}
        max={FMEA_MAX}
        step={1}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-valuemin={FMEA_MIN}
        aria-valuemax={FMEA_MAX}
        aria-valuenow={value}
      />
      <span className={styles.sliderValue} aria-live="polite">
        {value}
      </span>
    </label>
  );
}
